import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Shield,
  Heart,
  Lock,
  Eye,
  EyeOff,
  Users,
  Building2,
  CheckCircle2,
  XCircle,
  Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Privacy = () => {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPolicy();
  }, []);

  const fetchPolicy = async () => {
    try {
      const response = await axios.get(`${API}/privacy/policy`);
      setPolicy(response.data);
    } catch (error) {
      console.error('Failed to fetch privacy policy');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 rounded-full bg-primary/10 mx-auto mb-4 flex items-center justify-center">
              <Shield className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-4xl font-heading font-medium mb-4">Privacy & Data Protection</h1>
            <p className="text-muted-foreground text-lg">
              Your privacy matters to us. Here's how we protect your data.
            </p>
          </div>

          {/* Nonprofit Banner */}
          <Card className="mb-8 bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-heading font-medium mb-2">
                    All American Dog Club is a Nonprofit Organization
                  </h2>
                  <p className="text-muted-foreground mb-3">
                    We exist to serve the pet-loving community, not to profit from your data. 
                    Our mission is to connect pet owners, share knowledge, and support animal welfare. 
                    Any revenue from sponsors goes directly to maintaining and improving our platform.
                  </p>
                  <div className="bg-primary/10 rounded-lg p-4 border border-primary/20">
                    <p className="text-sm font-medium text-primary flex items-start gap-2">
                      <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong>Our Promise:</strong> As a nonprofit, AADC will never be sold to a commercial 
                        company that could exploit our community for profit. Your trust and data will always 
                        be protected by our nonprofit mission, not corporate interests.
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Key Privacy Points */}
          {policy?.data_policy?.key_points && (
            <div className="grid gap-4 mb-8">
              {policy.data_policy.key_points.map((point) => (
                <Card key={`keypoint-${point.title}`} className="card-interactive">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-medium mb-1">{point.title}</h3>
                        <p className="text-sm text-muted-foreground">{point.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* What We Share vs Don't Share */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* What We Share */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2 text-lg">
                  <Eye className="w-5 h-5 text-amber-500" />
                  Data Shared with Sponsors
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Only anonymous, aggregated data:
                </p>
                <ul className="space-y-2">
                  {policy?.sponsor_data_shared?.map((item) => (
                    <li key={`shared-${item}`} className="flex items-start gap-2 text-sm">
                      <Badge variant="outline" className="mt-0.5 bg-amber-50 text-amber-700 border-amber-200">
                        Anonymous
                      </Badge>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* What We Don't Share */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2 text-lg">
                  <EyeOff className="w-5 h-5 text-green-500" />
                  Data NEVER Shared
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Your personal information stays private:
                </p>
                <ul className="space-y-2">
                  {policy?.sponsor_data_never_shared?.map((item) => (
                    <li key={`never-${item}`} className="flex items-center gap-2 text-sm">
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Additional Info */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Lock className="w-5 h-5" />
                How Advertising Works
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                AADC displays advertisements from sponsors to help fund our nonprofit mission. 
                Here's how we handle advertising responsibly:
              </p>
              
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-medium mb-2">For Registered Members:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• You can choose your ad preferences in Settings</li>
                  <li>• Full members can opt out of ads entirely</li>
                  <li>• Ads may be shown based on your selected interests (never personal data)</li>
                </ul>
              </div>
              
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-medium mb-2">For Visitors (Not Logged In):</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• General ads are displayed</li>
                  <li>• No tracking or personal data collection</li>
                  <li>• Register for an ad-free or personalized experience</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card>
            <CardContent className="p-6 text-center">
              <Users className="w-8 h-8 text-primary mx-auto mb-3" />
              <h3 className="font-heading text-lg font-medium mb-2">Questions About Privacy?</h3>
              <p className="text-muted-foreground text-sm">
                We're committed to transparency. If you have any questions about how we handle your data, 
                please reach out to our community support team.
              </p>
            </CardContent>
          </Card>

          {/* Footer Notice */}
          <div className="mt-8 text-center text-sm text-muted-foreground">
            <p>Last updated: December 2025</p>
            <p className="mt-1">
              All American Dog Club • A 501(c)(3) Nonprofit Organization
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
